import { enableProdMode, importProvidersFrom, APP_INITIALIZER } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { AppComponent } from './app/app.component';
import { routes } from './app/app-routing.module';
import { environment } from './environments/environment';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth, connectAuthEmulator } from '@angular/fire/auth';
import { provideFirestore, getFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AppMaterialModule } from '@tamilarasitech/tt-branding-app-lib';
import { TtUIComponentsModule } from '@tamilarasitech/tt-branding-app-lib';
import { APP_CONFIG, ENVIRONMENT, BrandingService } from '@tamilarasitech/tt-branding-app-lib';
import { DatePipe } from '@angular/common';
import { appConfig } from './app/app.config';
import { authTokenInterceptor } from './app/core/interceptors/auth-token.interceptor';

// Platform default branding colors (from marketing-app)
const PLATFORM_DEFAULTS = {
  primaryColor: '#1A237E',   // Deep Navy
  accentColor: '#E67E22',    // Sunset Orange
  warnColor: '#F44336',      // Bright Coral
};

function applyMaterialThemeVarsFromBrandingConfig(config: any) {
  const primary = config?.theme?.mainColors?.primaryColor ?? PLATFORM_DEFAULTS.primaryColor;
  const accent = config?.theme?.mainColors?.accentColor ?? PLATFORM_DEFAULTS.accentColor;
  const warn = config?.theme?.mainColors?.warnColor ?? PLATFORM_DEFAULTS.warnColor;

  if (typeof document === 'undefined') {
    return;
  }

  const rootStyle = document.documentElement.style;
  if (primary) {
    rootStyle.setProperty('--mat-sys-primary', primary);
    rootStyle.setProperty('--md-sys-color-primary', primary);
    rootStyle.setProperty('--mat-sys-primary-container', primary);
    rootStyle.setProperty('--md-sys-color-primary-container', primary);
    // Set on-primary for text contrast (white on dark primary backgrounds)
    rootStyle.setProperty('--mat-sys-on-primary', '#FFFFFF');
    rootStyle.setProperty('--mat-sys-on-primary-container', '#FFFFFF');
  }
  if (accent) {
    rootStyle.setProperty('--mat-sys-tertiary', accent);
    rootStyle.setProperty('--md-sys-color-tertiary', accent);
  }
  if (warn) {
    rootStyle.setProperty('--mat-sys-error', warn);
    rootStyle.setProperty('--md-sys-color-error', warn);
  }
}

function initializeBranding(brandingService: BrandingService) {
  return () => {
    return brandingService.load().then(() => {
      applyMaterialThemeVarsFromBrandingConfig(brandingService.config);
      return true;
    });
  };
}

// function initializeTheming(themingService: ThemingService) {
//   return () => themingService.initialize();
// }

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([authTokenInterceptor])),
    DatePipe,
    { provide: APP_CONFIG, useValue: appConfig },
    {
      provide: ENVIRONMENT,
      useValue: {
        ...environment,
        isMultiTenant: false,
        backendApi: {
          baseUrl: environment.baseApiUrl,
        },
      },
    },
    BrandingService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeBranding,
      deps: [BrandingService],
      multi: true,
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initializeTheming,
    //   deps: [ThemingService],
    //   multi: true,
    // },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
      }
      return firestore;
    }),
    provideStorage(() => getStorage()),
    provideDatabase(() => getDatabase()),
    importProvidersFrom(
      AngularFireModule.initializeApp(environment.firebase),
      AngularFirestoreModule,
      AppMaterialModule,
      TtUIComponentsModule
    ),
  ],
}).catch((err) => console.error(err));
